import { type LatLngTuple } from 'leaflet';
import { useEffect, useState } from 'react';

import { type DrivingRoute, fetchDrivingRoute } from '../helpers/ignRouting';

type TourInput = { approId: string; positions: LatLngTuple[] };

// A tour's signature = its (rounded) stop sequence. Changes on reorder (→ targeted refetch) and stays stable
// across re-renders (focus, selection) → one request per tour, not one per render.
const signatureOf = (positions: LatLngTuple[]): string =>
    positions.map((point) => `${point[0].toFixed(5)},${point[1].toFixed(5)}`).join('|');

// Cache shared across renders and instances. `null` = known failure: we don't refetch (the caller falls back
// to the straight line), which avoids hammering an invalid route or wasting the quota.
const routeCache = new Map<string, DrivingRoute | null>();

// For each tour (≥ 2 stops), fetches the real road geometry from the IGN API, memoised by signature.
// Returns a Map approId → route; a missing tour ⇒ straight-line fallback on the map.
export const useTourRoutes = (tours: TourInput[]): Map<string, DrivingRoute> => {
    const [routes, setRoutes] = useState<Map<string, DrivingRoute>>(new Map());

    // Stable effect key: the effect only re-runs when a tour's order/coordinates actually change.
    const effectKey = tours
        .filter((tour) => tour.positions.length >= 2)
        .map((tour) => `${tour.approId}:${signatureOf(tour.positions)}`)
        .join('~');

    useEffect(() => {
        const controller = new AbortController();
        let cancelled = false;

        const routable = tours.filter((tour) => tour.positions.length >= 2);
        const resolved = new Map<string, DrivingRoute>();
        const missing: TourInput[] = [];
        for (const tour of routable) {
            const cached = routeCache.get(signatureOf(tour.positions));
            if (cached) resolved.set(tour.approId, cached);
            else if (cached === undefined) missing.push(tour);
        }
        // Reuse what's already cached right away (no blank screen when toggling focus).
        setRoutes(resolved);

        // Fetch the missing tours sequentially: well under the 5 req/s quota.
        const run = async (): Promise<void> => {
            for (const tour of missing) {
                const route = await fetchDrivingRoute(tour.positions, controller.signal);
                if (cancelled) return;
                routeCache.set(signatureOf(tour.positions), route);
                if (route) setRoutes((current) => new Map(current).set(tour.approId, route));
            }
        };
        void run();

        return () => {
            cancelled = true;
            controller.abort();
        };
        // `tours` is intentionally out of deps: `effectKey` captures the only change that must re-trigger the fetch.
    }, [effectKey]);

    return routes;
};
